// GA4 Analytics Utility - Enterprise Setup
// Measurement ID: G-4SRBZG2Y6D

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const GA_MEASUREMENT_ID = 'G-4SRBZG2Y6D';

// Conversion values
const CONVERSION_VALUES = {
  consultation_booking: 100, // £100 per consultation
  contact_form: 20,          // £20 per contact
} as const;

// Content groupings for reporting
const PAGE_CATEGORIES: Record<string, string> = {
  '/': 'Home',
  '/about': 'About',
  '/services': 'Services',
  '/how-it-works': 'How It Works',
  '/partnership': 'Partnership',
  '/discovery': 'Discovery',
  '/opportunities': 'Opportunities',
  '/reports': 'Reports',
  '/book': 'Booking',
  '/contact': 'Contact',
  '/privacy-policy': 'Legal',
  '/cookie-policy': 'Legal',
  '/terms-of-service': 'Legal',
};

// Helper to safely call gtag
const gtag = (...args: any[]) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args);
  }
};

// ============================================
// PAGE VIEW TRACKING
// ============================================

export const trackPageView = (path: string, title?: string) => {
  const pageCategory = PAGE_CATEGORIES[path] || 'Other';

  gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
    content_group: pageCategory,
  });
};

// ============================================
// USER PROPERTIES
// ============================================

export const setUserProperties = (properties: {
  user_type?: 'client' | 'guest';
  user_id?: string;
  is_authenticated?: boolean;
}) => {
  gtag('set', 'user_properties', {
    user_type: properties.user_type || 'guest',
    is_authenticated: properties.is_authenticated || false,
  });

  if (properties.user_id) {
    gtag('config', GA_MEASUREMENT_ID, {
      user_id: properties.user_id,
    });
  }
};

// ============================================
// CONVERSION TRACKING
// ============================================

// Consultation Booking Conversion (£100 value)
export const trackBookingConversion = (bookingDetails: {
  booking_id?: string;
  meeting_type?: string;
  booking_date?: string;
  booking_time?: string;
}) => {
  gtag('event', 'generate_lead', {
    currency: 'GBP',
    value: CONVERSION_VALUES.consultation_booking,
    lead_type: 'consultation_booking',
    ...bookingDetails,
  });

  // Also track as custom conversion event
  gtag('event', 'consultation_booked', {
    currency: 'GBP',
    value: CONVERSION_VALUES.consultation_booking,
    ...bookingDetails,
  });
};

// Contact Form Conversion (£20 value)
export const trackContactConversion = (contactDetails: {
  form_type?: string;
  inquiry_type?: string;
}) => {
  gtag('event', 'generate_lead', {
    currency: 'GBP',
    value: CONVERSION_VALUES.contact_form,
    lead_type: 'contact_form',
    ...contactDetails,
  });

  // Also track as custom conversion event
  gtag('event', 'contact_submitted', {
    currency: 'GBP',
    value: CONVERSION_VALUES.contact_form,
    ...contactDetails,
  });
};

// ============================================
// BOOKING FUNNEL TRACKING
// ============================================

export const trackBookingFunnel = {
  // Funnel start
  start: () => {
    gtag('event', 'begin_checkout', {
      currency: 'GBP',
      value: CONVERSION_VALUES.consultation_booking,
      items: [{ item_name: 'Consultation Booking', item_category: 'Services' }],
    });
  },

  // Step progression
  step: (stepNumber: number, stepName: string) => {
    gtag('event', 'booking_step', {
      step_number: stepNumber,
      step_name: stepName,
    });
  },

  // Date selected
  dateSelected: (date: string) => {
    gtag('event', 'booking_date_selected', {
      selected_date: date,
    });
  },

  // Time selected
  timeSelected: (time: string, date: string) => {
    gtag('event', 'booking_time_selected', {
      selected_time: time,
      selected_date: date,
    });
  },

  // Form started
  formStarted: () => {
    gtag('event', 'booking_form_started');
  },

  // Form field interaction
  fieldInteraction: (fieldName: string) => {
    gtag('event', 'form_field_interaction', {
      form_name: 'booking',
      field_name: fieldName,
    });
  },

  // Abandonment (before completion)
  abandoned: (lastStep: string) => {
    gtag('event', 'booking_abandoned', {
      last_step: lastStep,
    });
  },

  // Completion (calls trackBookingConversion)
  complete: (details: Parameters<typeof trackBookingConversion>[0]) => {
    trackBookingConversion(details);
  },
};

// ============================================
// CONTACT FORM TRACKING
// ============================================

export const trackContactForm = {
  // Form viewed
  viewed: () => {
    gtag('event', 'contact_form_view');
  },

  // Form started (first interaction)
  started: () => {
    gtag('event', 'contact_form_start');
  },

  // Field interaction
  fieldInteraction: (fieldName: string) => {
    gtag('event', 'form_field_interaction', {
      form_name: 'contact',
      field_name: fieldName,
    });
  },

  // Form submitted
  submitted: (details?: { inquiry_type?: string }) => {
    trackContactConversion({
      form_type: 'contact_page',
      ...details,
    });
  },

  // Form error
  error: (errorType: string) => {
    gtag('event', 'form_error', {
      form_name: 'contact',
      error_type: errorType,
    });
  },
};

// ============================================
// CTA & NAVIGATION TRACKING
// ============================================

export const trackCTA = (ctaName: string, ctaLocation: string, destination?: string) => {
  gtag('event', 'cta_click', {
    cta_name: ctaName,
    cta_location: ctaLocation,
    destination: destination,
  });
};

export const trackNavigation = (linkText: string, destination: string, navType: 'header' | 'footer' | 'inline') => {
  gtag('event', 'navigation_click', {
    link_text: linkText,
    destination: destination,
    nav_type: navType,
  });
};

export const trackExternalLink = (url: string, linkText?: string) => {
  gtag('event', 'click', {
    event_category: 'outbound',
    event_label: url,
    link_text: linkText,
    transport_type: 'beacon',
  });
};

// ============================================
// CONTENT ENGAGEMENT
// ============================================

export const trackContentEngagement = {
  // Scroll depth
  scrollDepth: (percentage: number, pagePath: string) => {
    gtag('event', 'scroll', {
      percent_scrolled: percentage,
      page_path: pagePath,
    });
  },

  // Accordion/expandable content
  expandContent: (contentId: string, contentTitle: string) => {
    gtag('event', 'expand_content', {
      content_id: contentId,
      content_title: contentTitle,
    });
  },

  // Modal views
  modalView: (modalName: string) => {
    gtag('event', 'modal_view', {
      modal_name: modalName,
    });
  },

  // Image gallery
  galleryInteraction: (action: 'view' | 'next' | 'prev', imageName?: string) => {
    gtag('event', 'gallery_interaction', {
      action: action,
      image_name: imageName,
    });
  },

  // Theme toggle
  themeToggle: (theme: 'light' | 'dark') => {
    gtag('event', 'theme_change', {
      theme: theme,
    });
  },
};

// ============================================
// ERROR TRACKING
// ============================================

export const trackError = (errorType: string, errorMessage: string, errorLocation?: string) => {
  gtag('event', 'exception', {
    description: `${errorType}: ${errorMessage}`,
    fatal: false,
    error_location: errorLocation,
  });
};

export const trackAPIError = (endpoint: string, statusCode: number, errorMessage?: string) => {
  gtag('event', 'api_error', {
    endpoint: endpoint,
    status_code: statusCode,
    error_message: errorMessage,
  });
};

// ============================================
// UTM PARAMETER HANDLING
// ============================================

export const getUTMParams = () => {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content'),
  };
};

export const trackCampaignLanding = () => {
  const utmParams = getUTMParams();

  if (utmParams.utm_source) {
    gtag('event', 'campaign_landing', {
      ...utmParams,
      landing_page: window.location.pathname,
    });
  }
};

// ============================================
// CORE WEB VITALS
// ============================================

export const trackWebVitals = (metric: {
  name: string;
  value: number;
  id: string;
}) => {
  gtag('event', metric.name, {
    event_category: 'Web Vitals',
    event_label: metric.id,
    value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
    non_interaction: true,
  });
};

// ============================================
// SESSION TRACKING
// ============================================

export const trackSessionStart = () => {
  const isReturning = localStorage.getItem('novahass_visited') === 'true';

  gtag('set', 'user_properties', {
    visitor_type: isReturning ? 'returning' : 'new',
  });

  if (!isReturning) {
    localStorage.setItem('novahass_visited', 'true');
    gtag('event', 'first_visit');
  }
};

// ============================================
// OPPORTUNITY/PROPERTY TRACKING (Enhanced Ecommerce style)
// ============================================

export const trackOpportunity = {
  // View opportunity list
  viewList: (opportunities: Array<{ id: string; name: string; category?: string }>) => {
    gtag('event', 'view_item_list', {
      item_list_name: 'Opportunities',
      items: opportunities.map((opp, index) => ({
        item_id: opp.id,
        item_name: opp.name,
        item_category: opp.category || 'Property',
        index: index,
      })),
    });
  },

  // View single opportunity
  viewItem: (opportunity: { id: string; name: string; category?: string; value?: number }) => {
    gtag('event', 'view_item', {
      currency: 'GBP',
      value: opportunity.value || 0,
      items: [{
        item_id: opportunity.id,
        item_name: opportunity.name,
        item_category: opportunity.category || 'Property',
      }],
    });
  },

  // Express interest
  expressInterest: (opportunity: { id: string; name: string }) => {
    gtag('event', 'add_to_wishlist', {
      currency: 'GBP',
      items: [{
        item_id: opportunity.id,
        item_name: opportunity.name,
      }],
    });
  },
};
